import React, { useCallback, useEffect, useState } from 'react';
import { ReviewItem, getReviewQueue, confirmReview, rejectReview } from '../api/voice';
import './ReviewQueue.css';

const ReviewQueue: React.FC = () => {
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await getReviewQueue());
    } catch (err: any) {
      setError(err?.message || 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id: string, fn: (id: string) => Promise<void>) => {
    setActingId(id);
    setError(null);
    try {
      await fn(id);
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err: any) {
      setError(err?.message || 'Action failed');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="review-page">
      <div className="review-title-row">
        <h1>Review queue</h1>
        {!loading && items.length > 0 && (
          <span className="review-count">{items.length} pending</span>
        )}
      </div>
      <p className="review-intro">
        Blueprint rules and newly-seen entities wait here for your confirmation before they
        commit. This is the one mandatory touchpoint that keeps the strategy blueprint clean.
      </p>

      {error && <div className="review-error">{error}</div>}

      {loading ? (
        <div className="review-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="review-empty">
          <span className="review-empty-icon">✓</span>
          Nothing to review. Sort a transcribed voice note into records to populate this queue.
        </div>
      ) : (
        <ul className="review-list">
          {items.map((it) => (
            <li key={it.id} className={`review-item review-item--${it.kind}`}>
              <div className="review-item-main">
                <span className={`review-kind review-kind--${it.kind}`}>
                  {it.kind === 'rule' ? it.rule_type?.replace('_', ' ') || 'RULE' : `New ${it.entity_type || 'entity'}`}
                </span>
                <span className="review-text">{it.text}</span>
              </div>
              <div className="review-item-foot">
                {it.username && (
                  <span className="review-owner" title="Proposed by">
                    {it.username}
                  </span>
                )}
                {it.kind === 'rule' && it.parametric_status && (
                  <span
                    className={`review-param review-param--${it.parametric_status}`}
                    title="Whether the rule could be turned into an executable parametric form"
                  >
                    {it.parametric_status === 'parsed'
                      ? 'parametric: parsed'
                      : it.parametric_status === 'unparseable'
                      ? 'parametric: unparseable'
                      : 'parametric: needs review'}
                  </span>
                )}
                <span className="review-spacer" />
                <button
                  className="review-btn review-btn--reject"
                  onClick={() => act(it.id, rejectReview)}
                  disabled={actingId === it.id}
                >
                  Reject
                </button>
                <button
                  className="review-btn review-btn--confirm"
                  onClick={() => act(it.id, confirmReview)}
                  disabled={actingId === it.id}
                >
                  {actingId === it.id ? '…' : 'Confirm'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ReviewQueue;
